import { useContext, useEffect, useRef, useState } from "react";
import { useGame } from "../../state/GameProvider";
import { GameSurfaceVisibility } from "../../state/GameSurfaceVisibility";

/** One cancelable timer; deliberate interaction pauses it for reading. */
export function AutoAdvance({ advance }: { advance: () => void }) {
  const { preferences } = useGame();
  const surfaceVisible = useContext(GameSurfaceVisibility);
  const [paused, setPaused] = useState(false);
  const [seconds, setSeconds] = useState(8);
  const callback = useRef(advance);
  callback.current = advance;
  useEffect(() => {
    if (!preferences.autoAdvance || paused) return;
    if (!surfaceVisible) { setPaused(true); return; }
    const host = window as Window & { AnalectsHostBridge?: { readonly visible: boolean } };
    if (host.AnalectsHostBridge?.visible === false) { setPaused(true); return; }
    const pause = (event?: Event) => {
      if (event?.target instanceof Element && event.target.closest(".advance-control")) return;
      setPaused(true);
    };
    const visibility = () => { if (document.hidden) pause(); };
    document.addEventListener("pointerdown", pause);
    document.addEventListener("keydown", pause);
    document.addEventListener("wheel", pause, { passive: true });
    document.addEventListener("touchstart", pause, { passive: true });
    document.addEventListener("visibilitychange", visibility);
    const hostVisibility = () => { if (host.AnalectsHostBridge?.visible === false) pause(); };
    window.addEventListener("analects:visibility", hostVisibility);
    return () => {
      document.removeEventListener("pointerdown", pause);
      document.removeEventListener("keydown", pause);
      document.removeEventListener("wheel", pause);
      document.removeEventListener("touchstart", pause);
      document.removeEventListener("visibilitychange", visibility);
      window.removeEventListener("analects:visibility", hostVisibility);
    };
  }, [preferences.autoAdvance, paused, surfaceVisible]);
  useEffect(() => {
    if (!preferences.autoAdvance || paused || !surfaceVisible) return;
    if (seconds === 0) { callback.current(); return; }
    const timer = window.setTimeout(() => setSeconds(value => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [preferences.autoAdvance, paused, seconds, surfaceVisible]);
  if (!preferences.autoAdvance) return <p className="reading-note">讀完解析後，再繼續。</p>;
  return <div className="advance-control">
    <span aria-live="off">{paused ? "自動前進已暫停" : `${seconds} 秒後自動繼續`}</span>
    <button type="button" onClick={() => { setSeconds(8); setPaused(value => !value); }}>
      {paused ? "恢復倒數" : "暫停閱讀"}
    </button>
  </div>;
}
